/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
import { Component, ContentChildren, ElementRef, EventEmitter, forwardRef, Input, Output, QueryList, TemplateRef, ViewChild } from "@angular/core";
import { slideMotion } from "ng-zorro-antd/core/animation";
import { NzSelectComponent } from "ng-zorro-antd/select";
import { InputBoolean } from "ng-zorro-antd/core/util";
import { NzSafeAny } from "ng-zorro-antd/core/types";
import { WithConfig } from "ng-zorro-antd/core/config";
import { UiFilterOptionType, UiSelectItemInterface, UiSelectModeType, UiSelectOptionInterface, UiSelectSizeType } from "@webapp/ui/select/select.models";
import { uiSelectDefaultFilterOption } from "@webapp/ui/select/utils/select.utils";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { CdkConnectedOverlay, CdkOverlayOrigin } from "@angular/cdk/overlay";
import { UiSelectTopControlComponent } from "@webapp/ui/select/components/select-top-control/select-top-control.component";
import { UiOptionGroupComponent } from "@webapp/ui/select/components/option-group/option-group.component";
import { UiOptionComponent } from "@webapp/ui/select/components/option/option.component";

@Component({
  selector: "ui-select",
  exportAs: "uiSelect",
  preserveWhitespaces: false,
  animations: [slideMotion],
  templateUrl: "select.component.html",
  host: {
    "[class.ant-select-lg]": 'nzSize === "large"',
    "[class.ant-select-sm]": 'nzSize === "small"',
    "[class.ant-select-show-arrow]": `nzShowArrow`,
    "[class.ant-select-disabled]": "nzDisabled",
    "[class.ant-select-show-search]": `(nzShowSearch || nzMode !== 'default') && !nzDisabled`,
    "[class.ant-select-allow-clear]": "nzAllowClear",
    "[class.ant-select-borderless]": "nzBorderless",
    "[class.ant-select-open]": "nzOpen",
    "[class.ant-select-focused]": "nzOpen || focused",
    "[class.ant-select-single]": `nzMode === 'default'`,
    "[class.ant-select-multiple]": `nzMode !== 'default'`,
    "[class.ant-select-rtl]": `dir === 'rtl'`,
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiSelectComponent),
      multi: true,
    },
  ],
})
export class UiSelectComponent extends NzSelectComponent {
  @Input("selectId") nzId: string | null = null;
  @Input("selectSize") nzSize: UiSelectSizeType = "default";
  @Input("selectOptionHeightPx") nzOptionHeightPx = 32;
  @Input("selectOptionOverflowSize") nzOptionOverflowSize = 8;
  @Input("selectDropdownClassName") nzDropdownClassName: string | null = null;
  @Input("selectDropdownMatchSelectWidth") nzDropdownMatchSelectWidth = true;
  @Input("selectDropdownStyle") nzDropdownStyle: { [key: string]: string } | null = null;
  @Input("selectNotFoundContent") nzNotFoundContent: string | TemplateRef<NzSafeAny> | undefined = undefined;
  @Input("selectPlaceHolder") nzPlaceHolder: string | TemplateRef<NzSafeAny> | null = null;
  @Input("selectMaxTagCount") nzMaxTagCount = Infinity;
  @Input("selectDropdownRender") nzDropdownRender: TemplateRef<NzSafeAny> | null = null;
  @Input("selectCustomTemplate") nzCustomTemplate: TemplateRef<{ $implicit: UiSelectItemInterface }> | null = null;
  @Input("selectSuffixIcon")
  @WithConfig<TemplateRef<NzSafeAny> | string | null>()
  nzSuffixIcon: TemplateRef<NzSafeAny> | string | null = null;
  @Input("selectClearIcon") nzClearIcon: TemplateRef<NzSafeAny> | null = null;
  @Input("selectRemoveIcon") nzRemoveIcon: TemplateRef<NzSafeAny> | null = null;
  @Input("selectMenuItemSelectedIcon") nzMenuItemSelectedIcon: TemplateRef<NzSafeAny> | null = null;
  @Input("selectTokenSeparators") nzTokenSeparators: string[] = [];
  @Input("selectMaxTagPlaceholder") nzMaxTagPlaceholder: TemplateRef<{ $implicit: NzSafeAny[] }> | null = null;
  @Input("selectMaxMultipleCount") nzMaxMultipleCount = Infinity;
  @Input("selectMode") nzMode: UiSelectModeType = "default";
  @Input("selectFilterOption") nzFilterOption: UiFilterOptionType = uiSelectDefaultFilterOption;
  @Input("selectCompareWith") compareWith: (o1: NzSafeAny, o2: NzSafeAny) => boolean = (o1: NzSafeAny, o2: NzSafeAny) => o1 === o2;
  @Input("selectAllowClear") @InputBoolean() nzAllowClear = false;
  @Input("selectBorderless") @WithConfig<boolean>() @InputBoolean() nzBorderless = false;
  @Input("selectShowSearch") @InputBoolean() nzShowSearch = false;
  @Input("selectLoading") @InputBoolean() nzLoading = false;
  @Input("selectAutoFocus") @InputBoolean() nzAutoFocus = false;
  @Input("selectAutoClearSearchValue") @InputBoolean() nzAutoClearSearchValue = true;
  @Input("selectServerSearch") @InputBoolean() nzServerSearch = false;
  @Input("selectDisabled") @InputBoolean() nzDisabled = false;
  @Input("selectOpen") @InputBoolean() nzOpen = false;
  @Input("selectBackdrop") @WithConfig<boolean>() @InputBoolean() nzBackdrop = false;
  @Input("selectOptions") nzOptions: UiSelectOptionInterface[] = [];

  @Input("selectShowArrow")
  set selectShowArrow(value: boolean) {
    this.nzShowArrow = value;
  }
  get selectShowArrow(): boolean {
    return this.nzShowArrow;
  }

  @Output("selectOnSearch") readonly nzOnSearch = new EventEmitter<string>();
  @Output("selectScrollToBottom") readonly nzScrollToBottom = new EventEmitter<void>();
  @Output("selectOpenChange") readonly nzOpenChange = new EventEmitter<boolean>();
  @Output("selectBlur") readonly nzBlur = new EventEmitter<void>();
  @Output("selectFocus") readonly nzFocus = new EventEmitter<void>();

  @ViewChild(CdkOverlayOrigin, { static: true, read: ElementRef }) originElement!: ElementRef;
  @ViewChild(CdkConnectedOverlay, { static: true }) cdkConnectedOverlay!: CdkConnectedOverlay;
  @ViewChild(UiSelectTopControlComponent, { static: true }) nzSelectTopControlComponent!: UiSelectTopControlComponent;
  @ContentChildren(UiOptionComponent, { descendants: true }) listOfNzOptionComponent!: QueryList<UiOptionComponent>;
  @ContentChildren(UiOptionGroupComponent, { descendants: true })
  listOfNzOptionGroupComponent!: QueryList<UiOptionGroupComponent>;
  @ViewChild(UiOptionGroupComponent, { static: true, read: ElementRef }) nzOptionGroupComponentElement!: ElementRef;
  @ViewChild(UiSelectTopControlComponent, { static: true, read: ElementRef })
  nzSelectTopControlComponentElement!: ElementRef;
}
